// apps/console/src/services/auth/cognito-client.ts
//
// Plan 4 Task 3 — extracted Cognito client. Pure file move from the
// previous apps/console/src/services/auth.ts; zero behaviour change.
//
// The only semantic shift is the SDK client + config reads now go through
// the runtime /config.json loader (apps/console/src/services/auth/config.ts,
// Plan 4 Task 2) instead of a build-time `import config from
// '../cognito-config.json'`. The SDK client is lazy-constructed on first call
// rather than at module-load, so it can pull region from the runtime config
// once loadAuthConfig() has resolved.
//
// signIn keeps its existing throw-based MFA / NEW_PASSWORD_REQUIRED /
// TenantDeleted / TenantNotFound semantics. useAuth.tsx already
// try/catches these four error classes; the Task 7 factory will wrap signIn
// into the SignInResult discriminated union (types.ts::IFrontendAuthClient)
// for the cross-provider path. Until Task 7 lands, the existing throw-based
// callers (useAuth, Login, the 4 auth pages, profile MFA modals) keep
// working untouched via the thin re-export in ../auth.ts.

import {
  AssociateSoftwareTokenCommand,
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SetUserMFAPreferenceCommand,
  VerifySoftwareTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider'

import { env } from '../../config/env'
import { getAuthConfig } from './config'

let _client: CognitoIdentityProviderClient | null = null

function client(): CognitoIdentityProviderClient {
  if (!_client) {
    const cfg = getAuthConfig()

    if (cfg.provider !== 'cognito') {
      throw new Error('cognito-client.ts called under non-cognito provider')
    }

    _client = new CognitoIdentityProviderClient({ region: cfg.cognito.region })
  }

  return _client
}

function cognitoClientId(): string {
  const cfg = getAuthConfig()

  if (cfg.provider !== 'cognito') {
    throw new Error('cognito-client.ts called under non-cognito provider')
  }

  return cfg.cognito.clientId
}

const REFRESH_TOKEN_KEY = 'gisify_refresh_token'
const ACCESS_TOKEN_KEY = 'gisify_access_token'

/**
 * Thrown by `signIn` when Cognito returns a SOFTWARE_TOKEN_MFA challenge
 * instead of an AuthenticationResult. The caller must collect a 6-digit TOTP
 * code from the user and call `respondToMfaChallenge(email, code, session)`.
 */
export class MfaChallengeError extends Error {
  readonly kind = 'mfa-required'
  constructor(
    public session: string,
    public email: string,
  ) {
    super('MFA code required')
    this.name = 'MfaChallengeError'
  }
}

/**
 * Thrown by `signIn` when Cognito returns a NEW_PASSWORD_REQUIRED challenge —
 * the standard first-login flow for users created via `AdminCreateUser` with
 * a temporary password (the `tenants/create.ts` invite-email path). The caller
 * must collect a new password from the user and call
 * `respondToNewPasswordChallenge(email, newPassword, session)`.
 *
 * Without this branch, `signIn` falls through and returns an empty IdToken,
 * which surfaces to the user as a generic "Unauthorized" on the next API call.
 */
export class NewPasswordRequiredError extends Error {
  readonly kind = 'new-password-required'
  constructor(
    public session: string,
    public email: string,
  ) {
    super('New password required')
    this.name = 'NewPasswordRequiredError'
  }
}

/**
 * Issue #327A — thrown when Cognito rejects sign-in with
 * `NotAuthorizedException: User is disabled` AND the tenant-status probe
 * reports the tenant is in `deleted` state. The cascade-delete worker
 * (`services/console/tenants/src/handlers/cascade-delete-worker.ts`) calls
 * `AdminDisableUser` on every tenant identity post-soft-delete, which makes
 * Cognito reject sign-in BEFORE `/users/me` runs — so the existing
 * `mapTenantStatusError` post-login path never fires. We catch the disabled
 * error, probe `/tenants/status?tenantId=X`, and throw this when the
 * tenant is `deleted` so `useAuth.signIn` can route to /account-deleted.
 */
export class TenantDeletedError extends Error {
  readonly kind = 'tenant-deleted'
  constructor() {
    super('This account no longer exists. Please contact support@gisify.com.')
    this.name = 'TenantDeletedError'
  }
}

/**
 * Issue #259 — thrown when the user lands on a tenant subdomain
 * (`t22.console.gisify.dev`) whose tenantId doesn't exist in the database
 * at all. Surfaces via either:
 *   - Login.tsx mount pre-flight (probe runs before user submits)
 *   - signIn Cognito catch (defensive — if pre-flight missed the case)
 */
export class TenantNotFoundError extends Error {
  readonly kind = 'tenant-not-found'
  constructor() {
    super('This organization does not exist. Please check the URL or contact your administrator.')
    this.name = 'TenantNotFoundError'
  }
}

/**
 * Issue #327A / #259 — public, no-auth tenant-status probe. Used by signIn's
 * Cognito-error catch to disambiguate "user is disabled" vs "tenant was
 * deleted, all its users got disabled along with it" vs "tenant doesn't exist
 * at all".
 *
 * Calls `GET /tenants/status?tenantId=<subdomain>` against the central API
 * (NOT the tenant subdomain — the tenant subdomain may not exist). Returns
 * the parsed status or `'unknown'` on any error (network failure, malformed
 * response, etc.).
 */
export async function probeTenantStatus(tenantId: string): Promise<'active' | 'suspended' | 'deleted' | 'unknown'> {
  try {
    const baseDomain = (import.meta.env?.VITE_BASE_DOMAIN as string | undefined) ?? 'gisify.dev'
    const url = `https://console.api.${baseDomain}/tenants/status?tenantId=${encodeURIComponent(tenantId)}`
    const res = await fetch(url, { method: 'GET' })

    if (!res.ok) return 'unknown'
    const body = (await res.json()) as { isSuccess?: boolean; data?: { status?: string } }
    const status = body.data?.status

    if (status === 'active' || status === 'suspended' || status === 'deleted') return status

    return 'unknown'
  } catch {
    return 'unknown'
  }
}

/**
 * Extract the tenant prefix from `window.location.hostname`. Used by the
 * Cognito-error catch to decide whether to probe `/tenants/status`.
 * Returns null for non-tenant hosts (central / localhost).
 */
export function getTenantIdFromHost(): string | null {
  if (typeof window === 'undefined') return null
  const baseDomain = (import.meta.env?.VITE_BASE_DOMAIN as string | undefined) ?? 'gisify.dev'
  const consoleSuffix = `.console.${baseDomain}`

  if (window.location.hostname.endsWith(consoleSuffix)) {
    const prefix = window.location.hostname.slice(0, -consoleSuffix.length)

    if (prefix && !prefix.includes('.') && prefix !== 'www') return prefix
  }

  return null
}

export async function signIn(email: string, password: string): Promise<string> {
  const clientId = cognitoClientId()
  const command = new InitiateAuthCommand({
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: clientId,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  })
  let response

  try {
    response = await client().send(command)
  } catch (err) {
    // Issue #327A — when Cognito rejects sign-in with `User is disabled`
    // and we're on a tenant subdomain, the most likely cause is that the
    // tenant was soft-deleted (the cascade worker disabled every Cognito
    // user). Probe `/tenants/status` to confirm before showing the generic
    // banner. On confirmed `deleted` status, throw a typed error so
    // useAuth routes to /account-deleted with the proper messaging.
    if (err instanceof Error && /user is disabled/i.test(err.message)) {
      const tenantId = getTenantIdFromHost()

      if (tenantId) {
        const status = await probeTenantStatus(tenantId)

        if (status === 'deleted') throw new TenantDeletedError()
        if (status === 'unknown') throw new TenantNotFoundError()
      }
    }

    throw err
  }

  // Cognito returned a TOTP challenge — caller must collect a code and call respondToMfaChallenge
  if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA' && response.Session) {
    throw new MfaChallengeError(response.Session, email)
  }

  // First-login challenge for users created with a temporary password (Admin invite flow).
  // Caller must collect a new password and call respondToNewPasswordChallenge.
  if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED' && response.Session) {
    throw new NewPasswordRequiredError(response.Session, email)
  }

  const refreshToken = response.AuthenticationResult?.RefreshToken
  const accessToken = response.AuthenticationResult?.AccessToken

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }

  return response.AuthenticationResult?.IdToken ?? ''
}

/**
 * Respond to a `NEW_PASSWORD_REQUIRED` challenge with the user's new password.
 * Returns the IdToken (and stores AccessToken + RefreshToken) on success.
 * Throws `InvalidPasswordException` if the password violates the pool policy.
 */
export async function respondToNewPasswordChallenge(
  email: string,
  newPassword: string,
  session: string,
): Promise<string> {
  const command = new RespondToAuthChallengeCommand({
    ClientId: cognitoClientId(),
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    Session: session,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
    },
  })
  const response = await client().send(command)

  // Cognito may chain into an MFA challenge after the new password is set —
  // forward that out so the caller can render the MFA screen.
  if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA' && response.Session) {
    throw new MfaChallengeError(response.Session, email)
  }

  const refreshToken = response.AuthenticationResult?.RefreshToken
  const accessToken = response.AuthenticationResult?.AccessToken

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }

  return response.AuthenticationResult?.IdToken ?? ''
}

/**
 * Respond to a `SOFTWARE_TOKEN_MFA` challenge with the user's 6-digit TOTP code.
 * Returns the IdToken (and stores AccessToken + RefreshToken) on success.
 * Throws `CodeMismatchException` if the code is wrong.
 */
export async function respondToMfaChallenge(email: string, code: string, session: string): Promise<string> {
  const command = new RespondToAuthChallengeCommand({
    ClientId: cognitoClientId(),
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    Session: session,
    ChallengeResponses: {
      USERNAME: email,
      SOFTWARE_TOKEN_MFA_CODE: code,
    },
  })
  const response = await client().send(command)

  const refreshToken = response.AuthenticationResult?.RefreshToken
  const accessToken = response.AuthenticationResult?.AccessToken

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }

  return response.AuthenticationResult?.IdToken ?? ''
}

export async function signOut(): Promise<void> {
  const accessToken = getAccessToken()

  if (!accessToken) return

  // Sprint G — best-effort logout audit (never block the user-facing logout)
  try {
    await fetch(`${env.apiBaseUrl}/auth/log-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ event: 'logout' }),
    })
  } catch {
    // swallow — logout never blocks on audit
  }

  const command = new GlobalSignOutCommand({
    AccessToken: accessToken,
  })

  try {
    await client().send(command)
  } finally {
    clearStoredTokens()
  }
}

/**
 * Cognito errors that mean the REFRESH TOKEN ITSELF is permanently unusable —
 * revoked, expired, or its user is gone. Only these may destroy the stored
 * tokens; everything else (transport failure, 5xx, TooManyRequestsException)
 * is recoverable on a later attempt.
 */
const PERMANENT_REFRESH_REJECTIONS = new Set([
  'NotAuthorizedException',
  'InvalidRefreshTokenException',
  'UserNotFoundException',
])

/**
 * True only when Cognito DEFINITIVELY rejected the refresh token. Matches on
 * the SDK error name (`__type` is the raw wire shape some transports surface)
 * plus the OAuth `invalid_grant` wording, and is deliberately an allow-list:
 * an unrecognised failure is treated as recoverable, because the cost of
 * wrongly keeping a dead refresh token (one extra failed round) is far lower
 * than the cost of wrongly destroying a live one (a forced re-login).
 */
function isPermanentRefreshRejection(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false

  const { name, __type, message } = err as { name?: string; __type?: string; message?: string }

  if (PERMANENT_REFRESH_REJECTIONS.has(name ?? '') || PERMANENT_REFRESH_REJECTIONS.has(__type ?? '')) return true

  return typeof message === 'string' && /invalid_grant|invalid refresh token/i.test(message)
}

/**
 * Silent re-auth from the stored refresh token. Returns the new IdToken, or
 * `null` when the round failed.
 *
 * INVARIANT: the stored tokens are dropped ONLY on a definitive rejection of
 * the refresh token (see `isPermanentRefreshRejection`). A network blip, a
 * Cognito 5xx or a throttle used to land in the same bare `catch` and wipe
 * `gisify_refresh_token`, which turned a retryable hiccup into a forced
 * re-login — the refresh material was gone even though the session was still
 * valid. Transient failures now leave storage untouched so the next attempt
 * (the periodic refresh, or the api client's next 401) can recover.
 *
 * We still resolve to `null` rather than throwing on a transient failure:
 * `useAuth`'s periodic refresh treats a throw as "log the user out", which is
 * exactly the outcome this guard exists to avoid. Callers that need to tell
 * the two apart read whether a refresh token survived (see
 * `services/api/client.ts`).
 */
export async function refreshToken(): Promise<string | null> {
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

  if (!storedRefreshToken) {
    return null
  }

  try {
    const command = new InitiateAuthCommand({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: cognitoClientId(),
      AuthParameters: {
        REFRESH_TOKEN: storedRefreshToken,
      },
    })
    const response = await client().send(command)

    const newAccessToken = response.AuthenticationResult?.AccessToken

    if (newAccessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken)
    }

    return response.AuthenticationResult?.IdToken ?? null
  } catch (err) {
    if (isPermanentRefreshRejection(err)) {
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(ACCESS_TOKEN_KEY)
    }

    return null
  }
}

export async function forgotPassword(email: string): Promise<void> {
  const command = new ForgotPasswordCommand({
    ClientId: cognitoClientId(),
    Username: email,
  })
  await client().send(command)
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const command = new ConfirmForgotPasswordCommand({
    ClientId: cognitoClientId(),
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  })
  await client().send(command)
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function clearStoredTokens(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(ACCESS_TOKEN_KEY)
}

function requireAccessToken(): string {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available — please sign in again')
  }

  return token
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const command = new ChangePasswordCommand({
    AccessToken: requireAccessToken(),
    PreviousPassword: currentPassword,
    ProposedPassword: newPassword,
  })
  await client().send(command)
}

export async function associateSoftwareToken(): Promise<string> {
  const command = new AssociateSoftwareTokenCommand({
    AccessToken: requireAccessToken(),
  })
  const response = await client().send(command)

  if (!response.SecretCode) {
    throw new Error('Cognito did not return a secret code')
  }

  return response.SecretCode
}

export async function verifySoftwareToken(userCode: string): Promise<void> {
  const command = new VerifySoftwareTokenCommand({
    AccessToken: requireAccessToken(),
    UserCode: userCode,
  })
  await client().send(command)
}

export async function setUserMFAPreference(enabled: boolean): Promise<void> {
  const command = new SetUserMFAPreferenceCommand({
    AccessToken: requireAccessToken(),
    SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
  })
  await client().send(command)
}
